import { Injectable, inject } from "@angular/core";
import {
  Auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  User,
  GoogleAuthProvider,
  signInWithPopup,
  FacebookAuthProvider,
  sendPasswordResetEmail,
  updateProfile,
} from "@angular/fire/auth";
import { authState } from "@angular/fire/auth";
import { Observable } from "rxjs";
import { RolesService } from "./roles.service";
// import { CookieService } from "./cookie.service";

@Injectable({
  providedIn: "root",
})
export class FirebaseAuthService {
  private auth: Auth = inject(Auth);
  private rolesService = inject(RolesService);
  // private cookieService = inject(CookieService);
  user$: Observable<User | null> = authState(this.auth);

  constructor() {
    // Fetch roles when auth state changes (user logs in or page refreshes with logged-in user)
    this.user$.subscribe(async (user) => {
      if (user) {
        // Wait for user to get a valid token before fetching roles
        try {
          const token = await user.getIdToken();
          if (token) {
            console.log("🔑 Firebase token ready, fetching roles...");
            // Add a small delay to ensure auth state has propagated to interceptor
            setTimeout(async () => {
              await this.rolesService.fetchUserRoles();
              console.log("✅ User roles loaded");
            }, 100);
          }
        } catch (error) {
          console.error("Failed to get Firebase token:", error);
        }
      } else {
        // User is logged out, clear roles
        await this.rolesService.clearRoles();
      }
    });
  }

  async signInWithEmail(email: string, password: string) {
    try {
      const credential = await signInWithEmailAndPassword(
        this.auth,
        email,
        password,
      );
      return { success: true, user: credential.user };
    } catch (error: any) {
      console.error("Firebase Auth Error:", error.code, error.message);
      // Provide user-friendly error messages
      let errorMessage = "Login failed. Please try again.";
      switch (error.code) {
        case "auth/user-not-found":
          errorMessage = "No account found with this email address.";
          break;
        case "auth/wrong-password":
          errorMessage = "Incorrect password. Please try again.";
          break;
        case "auth/invalid-email":
          errorMessage = "Invalid email address format.";
          break;
        case "auth/user-disabled":
          errorMessage = "This account has been disabled.";
          break;
        case "auth/too-many-requests":
          errorMessage = "Too many failed attempts. Please try again later.";
          break;
        case "auth/operation-not-allowed":
          errorMessage =
            "Email/password sign-in is not enabled. Please contact support.";
          break;
        case "auth/invalid-credential":
          errorMessage = "Invalid email or password.";
          break;
        default:
          errorMessage = error.message || "Login failed. Please try again.";
      }
      return { success: false, error: errorMessage };
    }
  }

  async signUpWithEmail(email: string, password: string) {
    try {
      const credential = await createUserWithEmailAndPassword(
        this.auth,
        email,
        password,
      );
      return { success: true, user: credential.user };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  async signInWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      const credential = await signInWithPopup(this.auth, provider);
      return { success: true, user: credential.user };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  async signInWithFacebook() {
    try {
      const provider = new FacebookAuthProvider();
      const credential = await signInWithPopup(this.auth, provider);
      return { success: true, user: credential.user };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  async sendPasswordReset(email: string) {
    try {
      await sendPasswordResetEmail(this.auth, email);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  async signOut() {
    try {
      await signOut(this.auth);
      // Clear roles on sign out
      this.rolesService.clearRoles();

      // Delete session cookie (dev/test only)
      // This clears the environment authentication session used for admin panel
      // this.cookieService.delete("session");

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  getCurrentUser(): User | null {
    return this.auth.currentUser;
  }

  async getIdToken(): Promise<string | null> {
    try {
      const user = this.getCurrentUser();
      if (user) {
        return await user.getIdToken();
      }
      return null;
    } catch (error) {
      console.error("Error getting ID token:", error);
      return null;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    const user = this.getCurrentUser();
    if (!user) return false;

    try {
      await user.getIdToken();
      return true;
    } catch (error) {
      return false;
    }
  }

  async updateDisplayName(displayName: string) {
    try {
      const user = this.getCurrentUser();
      if (!user) {
        return { success: false, error: "No user logged in" };
      }

      await updateProfile(user, { displayName });
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }
}
